<template>
  <div></div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted } from "vue";

import { extractEmailIdFromUrl } from "@/lib/subscribers/extractEmailIdFromUrl";
import type { Newsletter } from "@/types/newsletter";

interface SubscriberData {
  id: string;
  type: string;
}

const props = defineProps<{
  newsletter: Newsletter;
}>();

let keydownHandler: ((e: KeyboardEvent) => void) | null = null;

onMounted(async () => {
  const subscriberElement = document.getElementById("subscriber");
  if (!subscriberElement) return;

  await import("@/subscriber_facing/styles/manage-subscription.css");

  const subscriber: SubscriberData = JSON.parse(
    subscriberElement.textContent || "{}"
  );

  const subscriptionButton = document.getElementById("subscription-button");
  const updateButton = document.getElementById("update-button");
  const saveChangesButton = document.getElementById("save-changes-button");
  const emailInput = document.getElementById(
    "email-input"
  ) as HTMLInputElement | null;
  const referralCodeInput = document.getElementById(
    "referral-code-input"
  ) as HTMLInputElement | null;
  const referralLink = document.getElementById("referral-link");
  const unsubscribeModal = document.getElementById("unsubscribe-modal");
  const modalCancelButton = document.getElementById("modal-cancel");
  const modalConfirmButton = document.getElementById("modal-confirm");
  const confirmationModal = document.getElementById("confirmation-modal");
  const confirmationModalRefreshButton = document.getElementById(
    "confirmation-modal-refresh"
  );

  function showModal() {
    if (!unsubscribeModal) return;
    unsubscribeModal.classList.remove("hidden");
    setTimeout(() => {
      unsubscribeModal.classList.add("show");
    }, 10);
  }

  function showConfirmationModal() {
    if (!confirmationModal) return;
    confirmationModal.classList.remove("hidden");
    setTimeout(() => {
      confirmationModal.classList.add("show");
    }, 10);
  }

  function hideModal() {
    if (!unsubscribeModal) return;
    unsubscribeModal.classList.remove("show");
    setTimeout(() => {
      unsubscribeModal.classList.add("hidden");
    }, 200);
  }

  // Auto-open unsubscribe modal from URL hash
  if (window.location.hash === "#unsubscribe") {
    if (subscriber.type !== "unsubscribed") {
      showModal();
    } else {
      window.history.replaceState(null, "", window.location.pathname);
    }
  }

  // Show confirmation modal for unactivated subscribers
  if (subscriber.type === "unactivated") {
    showConfirmationModal();
  }

  function processSubscriptionChange(
    button: HTMLElement,
    subscriberType: string
  ) {
    button.setAttribute("disabled", "true");
    button.innerText = "Processing...";

    const reasonInput = document.querySelector(
      'input[name="unsubscription-reason"]:checked'
    ) as HTMLInputElement | null;

    const payload: Record<string, string> = {
      type: subscriberType,
    };

    const unsubscriptionReason = reasonInput ? reasonInput.value : null;
    if (unsubscriptionReason) {
      payload.unsubscription_reason = unsubscriptionReason;
    }

    const email =
      window.location.search.includes("email=") &&
      subscriberType === "unsubscribed"
        ? window.location.search.split("email=")[1].split("&")[0]
        : null;
    const extractedEmailId = extractEmailIdFromUrl(email);
    if (extractedEmailId) {
      payload.email_which_prompted_unsubscription_id = extractedEmailId;
    }

    try {
      fetch("/v1/subscribers/" + subscriber.id, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      })
        .then((response) => response.json())
        .then(() => window.location.reload());
    } catch (error) {
      console.error("Error:", error);
      alert("An error occurred. Please try again.");
      button.removeAttribute("disabled");
      button.innerText =
        subscriberType === "unsubscribed" ? "Unsubscribe" : "Resubscribe";
    }
  }

  // Handle save changes button click
  if (saveChangesButton) {
    saveChangesButton.addEventListener("click", function () {
      const email = emailInput ? emailInput.value.trim() : null;
      const referralCode = referralCodeInput
        ? referralCodeInput.value.trim().toLowerCase()
        : null;

      if (email && !email.includes("@")) {
        alert("Please enter a valid email address");
        return;
      }

      if (
        referralCode &&
        (referralCode.length !== 6 || !/^[a-z]{6}$/.test(referralCode))
      ) {
        alert("Referral code must be exactly 6 letters (a-z)");
        return;
      }

      const requestBody: Record<string, string> = {
        newsletter_id: props.newsletter.id,
      };

      if (email) {
        requestBody.email = email;
      }

      if (referralCode) {
        requestBody.referral_code = referralCode;
      }

      saveChangesButton.setAttribute("disabled", "true");
      saveChangesButton.innerText = "Saving...";

      try {
        fetch("/v1/subscribers/" + subscriber.id, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        })
          .then((response) => {
            if (response.ok) {
              return response.json();
            } else {
              throw new Error("An error occurred. Please try again.");
            }
          })
          .then(() => {
            if (referralCode && referralLink) {
              referralLink.innerText =
                props.newsletter.absolute_url + "?utm_referral=" + referralCode;
            }

            window.location.reload();
          });
      } catch (error) {
        console.error("Error:", error);
        alert("An error occurred. Please try again.");
        window.location.reload();
      }
    });
  }

  // Handle subscription status change
  if (subscriptionButton) {
    subscriptionButton.addEventListener("click", function () {
      const action = (this as HTMLElement).dataset.action;

      if (action === "unsubscribe") {
        showModal();
        return;
      }

      processSubscriptionChange(subscriptionButton, "regular");
    });
  }

  // Handle modal cancel button
  if (modalCancelButton) {
    modalCancelButton.addEventListener("click", function () {
      hideModal();
    });
  }

  // Handle modal confirm button
  if (modalConfirmButton) {
    modalConfirmButton.addEventListener("click", function () {
      hideModal();
      processSubscriptionChange(subscriptionButton!, "unsubscribed");
    });
  }

  // Close modal when clicking outside
  if (unsubscribeModal) {
    unsubscribeModal.addEventListener("click", function (e) {
      if (e.target === unsubscribeModal) {
        hideModal();
      }
    });
  }

  // Handle confirmation modal refresh button
  if (confirmationModalRefreshButton) {
    confirmationModalRefreshButton.addEventListener("click", function () {
      window.location.reload();
    });
  }

  // Close modal with ESC key
  keydownHandler = function (e: KeyboardEvent) {
    if (
      e.key === "Escape" &&
      unsubscribeModal &&
      unsubscribeModal.classList.contains("show")
    ) {
      hideModal();
    }
  };
  document.addEventListener("keydown", keydownHandler);

  // Handle metadata update
  if (updateButton) {
    updateButton.addEventListener("click", function () {
      const metadataFields = document.querySelectorAll(".metadata-field");
      const metadata: Record<string, string | boolean> = {};

      metadataFields.forEach((field) => {
        const el = field as HTMLInputElement | HTMLSelectElement;
        const key = el.dataset.fieldKey;
        const type = el.dataset.fieldType;

        if (!key) return;

        if (type === "checkbox") {
          metadata[key] = (el as HTMLInputElement).checked;
        } else {
          metadata[key] = el.value;
        }
      });

      const tagFields = document.querySelectorAll(".tag-field");
      const tags: string[] = [];
      tagFields.forEach((field) => {
        const el = field as HTMLInputElement;
        if (el.checked) {
          tags.push(el.dataset.tagId!);
        }
      });

      updateButton.setAttribute("disabled", "true");
      updateButton.innerText = "Updating...";

      const requestBody: Record<string, unknown> = {
        newsletter_id: props.newsletter.id,
      };
      if (Object.keys(metadata).length > 0) {
        requestBody.metadata = metadata;
      }
      if (tagFields.length > 0) {
        requestBody.tags = tags;
      }

      fetch("/v1/subscribers/" + subscriber.id, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      }).then((response) => {
        if (response.ok) {
          window.location.reload();
        } else {
          alert("An error occurred. Please try again.");
          updateButton.removeAttribute("disabled");
          updateButton.innerText = "Update";
        }
      });
    });
  }
});

onUnmounted(() => {
  if (keydownHandler) {
    document.removeEventListener("keydown", keydownHandler);
  }
});
</script>
